/**
 * Shared eligibility async service for consistent job-based assessment flow.
 * Used by the admin eligibility assessment flow.
 */

import { apiService } from './api.js';

const IS_DEV = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
const devLog = (...args) => {
  if (IS_DEV) console.log(...args);
};
const devError = (...args) => {
  if (IS_DEV) console.error(...args);
};

/**
 * @typedef {Object} EligibilityJobResponse
 * @property {string} job_id
 * @property {'pending'|'running'|'done'|'error'} status
 * @property {string} created_at
 * @property {any} [result]
 * @property {string} [error]
 */

/**
 * @typedef {Object} EligibilityStatusResponse
 * @property {'pending'|'running'|'done'|'error'} status
 * @property {number} [progress]
 * @property {string} [error]
 * @property {any} [result]
 */

/**
 * @typedef {Object} EligibilityResult
 * @property {string} summary
 * @property {number} overall_score
 * @property {Array<Object>} grants
 * @property {Object} diagnostics
 */

class EligibilityAsyncService {
  constructor() {
    this.pollingInterval = 5000; // 5 seconds default polling
    this.maxPollingTime = 60000; // 60 seconds max polling duration
  }

  /**
   * Create a new eligibility assessment job.
   * Returns immediately with job_id for polling.
   * @param {any} formData
   * @param {any} userMeta
   * @returns {Promise<{jobId: string}>}
   */
  async createJob(formData, userMeta = null, flowOpts = {}) {
    devLog('🚀 Creating eligibility assessment job');
    
    try {
      const response = await apiService.startAssessment(formData, userMeta, flowOpts || {});
      
      if (!response || !response.job_id) {
        throw new Error('Failed to create assessment job - no job ID returned from backend');
      }
      
      const traceId = response.traceId || 'unknown';
      devLog(`[TRACE-${traceId}] ✅ Job created: ${response.job_id}`);
      
      return { 
        jobId: response.job_id,
        traceId: traceId,
        createdAt: response.created_at
      };
      
    } catch (error) {
      devError('❌ Failed to create assessment job:', error);
      // Not signed in: keep the backend's sentence as the message for the progress page.
      if (error?.name === 'AuthRequiredError') throw error;
      throw new Error(`Assessment creation failed: ${error.message}`);
    }
  }

  /**
   * Get the current status of an assessment job.
   * Returns status, progress, and error information.
   * @param {string} jobId
   * @returns {Promise<EligibilityStatusResponse>}
   */
  async getJobStatus(jobId, traceId = null, flowOpts = {}) {
    try {
      const response = await apiService.getAssessmentStatus(jobId, traceId, flowOpts || {});
      
      const trace = response.traceId || traceId || 'unknown';
      devLog(`[TRACE-${trace}] 📈 Job ${jobId} status: ${response.status}`);
      
      return response;
      
    } catch (error) {
      devError(`❌ Failed to get status for job ${jobId}:`, error.message);
      throw error;
    }
  }

  /**
   * Get the final result of a completed assessment job.
   * Only call this when status is 'done'.
   * @param {string} jobId
   * @returns {Promise<EligibilityResult>}
   */
  async getResult(jobId) {
    devLog(`📋 Getting assessment result: ${jobId}`);
    
    try {
      const statusResponse = await apiService.getAssessmentStatus(jobId, null, {});
      
      if (statusResponse.status !== 'done') {
        throw new Error(`Job not completed yet. Current status: ${statusResponse.status}`);
      }
      
      if (!statusResponse.result) {
        throw new Error('No result data available');
      }
      
      devLog('✅ Assessment result retrieved:', statusResponse.result);
      return statusResponse.result;
      
    } catch (error) {
      devError('❌ Failed to get assessment result:', error);
      throw error;
    }
  }

  /**
   * Download PDF for a completed assessment.
   * @param {string} jobId
   * @returns {Promise<void>}
   */
  async downloadPDF(jobId) {
    devLog(`📄 Downloading PDF for job: ${jobId}`);
    
    try {
      await apiService.downloadAssessmentPDF(jobId);
      devLog('✅ PDF download initiated');
      
    } catch (error) {
      devError('❌ Failed to download PDF:', error);
      throw error;
    }
  }

  /**
   * Create a synchronous assessment as fallback.
   * Use only when async job gets stuck.
   * @param {any} formData
   * @param {any} userMeta
   * @returns {Promise<EligibilityJobResponse>}
   */
  async createJobSync(formData, userMeta = null, flowOpts = {}) {
    devLog('🔄 Creating synchronous assessment (fallback):', formData);
    
    try {
      const response = await apiService.startAssessmentSync(formData, userMeta, flowOpts || {});
      
      devLog('✅ Synchronous assessment completed:', response);
      return response;
      
    } catch (error) {
      devError('❌ Synchronous assessment failed:', error);
      throw error;
    }
  }

  /**
   * Poll job status with exponential backoff.
   * Returns when job completes or fails.
   * @param {string} jobId
   * @param {function} onStatusUpdate
   * @param {number} maxPollingTime - Maximum time to wait (default 240s = 4 minutes)
   * @returns {Promise<EligibilityResult>}
   */
  async pollJobStatus(jobId, onStatusUpdate, maxPollingTime = 240000) {
    devLog(`🔄 Starting polling for job: ${jobId} (max wait: ${maxPollingTime / 1000}s)`);
    
    const startTime = Date.now();
    let pollingCount = 0;
    
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const elapsed = Date.now() - startTime;
          
          // Check timeout
          if (elapsed > maxPollingTime) {
            reject(new Error(`Assessment timeout after ${maxPollingTime / 1000}s`));
            return;
          }
          
          const statusResponse = await this.getJobStatus(jobId);
          pollingCount++;
          
          // Notify caller of status update
          onStatusUpdate(statusResponse);
          
          if (statusResponse.status === 'done') {
            devLog('✅ Job completed successfully');
            resolve(statusResponse.result);
            return;
          }
          
          if (statusResponse.status === 'error') {
            devError('❌ Job failed:', statusResponse.error);
            reject(new Error(statusResponse.error || 'Assessment failed'));
            return;
          }
          
          // Optimized polling intervals: 750ms → 1.25s → 2s → 2.5s (capped)
          let delay;
          if (pollingCount === 1) {
            delay = 750; // First poll: 750ms
          } else if (pollingCount === 2) {
            delay = 1250; // Second poll: 1.25s
          } else if (pollingCount === 3) {
            delay = 2000; // Third poll: 2s
          } else {
            delay = 2500; // All subsequent polls: 2.5s
          }
          
          devLog(`⏳ Job status: ${statusResponse.status}, next poll in ${delay / 1000}s (poll #${pollingCount})`);
          setTimeout(poll, delay);
          
        } catch (error) {
          devError('❌ Polling error:', error);
          
          // Retry with backoff on network errors
          const retryDelay = Math.min(3000 + pollingCount * 500, 10000);
          devLog(`⚠️ Polling failed, retrying in ${retryDelay / 1000}s...`);
          setTimeout(poll, retryDelay);
        }
      };
      
      // Start polling
      poll();
    });
  }
}

// Export singleton instance
export const eligibilityAsyncService = new EligibilityAsyncService();
export default eligibilityAsyncService;
